import { useState, useEffect } from 'react';
import { Map, TrendingUp, Mail, Globe, Phone, Calendar } from 'lucide-react';
import apiClient from '../utils/axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const touchpointIcons = {
  email: Mail,
  landing_page: Globe,
  linkedin: Globe,
  direct_mail: Mail,
  call: Phone,
  webinar: Calendar,
  event: Calendar
};

export default function AttributionVisualizer({ leadId, campaignId }) {
  const [attributionPath, setAttributionPath] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (leadId) {
      fetchAttributionPath();
    } else if (campaignId) {
      fetchCampaignAttribution();
    }
  }, [leadId, campaignId]);

  const fetchAttributionPath = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_BASE}/attribution/lead/${leadId}`);
      setAttributionPath(response.data);
    } catch (error) {
      console.error('Error fetching attribution path:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignAttribution = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_BASE}/attribution/campaign/${campaignId}`);
      setAttributionPath({
        touchpoints: response.data.touchpoints,
        attribution_by_type: response.data.attribution_by_type
      });
    } catch (error) {
      console.error('Error fetching campaign attribution:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-gray-500">Loading attribution data...</div>;
  }

  if (!attributionPath || !attributionPath.touchpoints || attributionPath.touchpoints.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        <Map size={48} className="mx-auto mb-4 text-gray-400" />
        <p>No touchpoints tracked yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Touchpoint Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Map size={20} />
          Touchpoint Journey
        </h3>
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

          {/* Touchpoints */}
          <div className="space-y-4">
            {attributionPath.touchpoints.map((touchpoint, index) => {
              const Icon = touchpointIcons[touchpoint.touchpoint_type] || Mail;
              const touchpointData = typeof touchpoint.touchpoint_data === 'string'
                ? JSON.parse(touchpoint.touchpoint_data)
                : touchpoint.touchpoint_data || {};

              return (
                <div key={touchpoint.id} className="relative flex items-start gap-4">
                  {/* Timeline Dot */}
                  <div className="relative z-10 w-16 h-16 bg-white rounded-full border-4 border-purple-500 flex items-center justify-center">
                    <Icon size={20} className="text-purple-600" />
                  </div>

                  {/* Touchpoint Content */}
                  <div className="flex-1 bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {touchpoint.touchpoint_type.charAt(0).toUpperCase() + touchpoint.touchpoint_type.slice(1).replace('_', ' ')}
                        </p>
                        <p className="text-sm text-gray-600">
                          {touchpoint.campaign_name || touchpoint.sequence_name || 'Nurture Sequence'}
                        </p>
                        {touchpointData.subject_line && (
                          <p className="text-xs text-gray-500 mt-1">"{touchpointData.subject_line}"</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-purple-600">
                          {parseFloat(touchpoint.attribution_weight || 0).toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-500">attribution</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(touchpoint.occurred_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Attribution Summary */}
      {attributionPath.attribution_by_type && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attribution by Type</h3>
          <div className="space-y-3">
            {attributionPath.attribution_by_type.map((type) => (
              <div key={type.touchpoint_type} className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {type.touchpoint_type.replace('_', ' ')}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {type.total_touchpoints} touchpoints
                  </span>
                  <span className="text-sm font-semibold text-purple-600">
                    {parseFloat(type.total_attribution_weight).toFixed(1)}% total
                  </span>
                  {type.conversions > 0 && (
                    <span className="text-sm text-green-600">
                      {type.conversions} conversions
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversion Status */}
      {attributionPath.conversion_status && (
        <div className={`bg-white rounded-lg shadow p-6 border-l-4 ${
          attributionPath.conversion_status === 'converted' ? 'border-green-500' : 'border-gray-300'
        }`}>
          <div className="flex items-center gap-2">
            <TrendingUp size={20} className={
              attributionPath.conversion_status === 'converted' ? 'text-green-600' : 'text-gray-400'
            } />
            <p className="font-semibold text-gray-900">
              Status: <span className={
                attributionPath.conversion_status === 'converted' ? 'text-green-600' : 'text-gray-600'
              }>
                {attributionPath.conversion_status === 'converted' ? 'Converted' : 'In Progress'}
              </span>
            </p>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {attributionPath.total_touchpoints} touchpoints in journey
          </p>
        </div>
      )}
    </div>
  );
}

